import { RangeCalendar } from "../components/RangeCalendar.js";
import DateField from "../components/DateField.js";
import { Container } from "../DatePicker/DatePicker.elements.js";
import { dateConverters, dateIsValid } from "../DatePicker/DatePicker.lib.js";
import DateRangeTimeFields from "../DateRangePicker/DateRangeTimeFields.js";
import React from "react";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
import { Controller, useFormContext } from "react-hook-form";
import { useDateRangePicker } from "react-aria";
import { useDateRangePickerState } from "react-stately";
import dayjs from "dayjs";
import "@internationalized/date";
//#region src/DatePicker/DateRangePickerWidget/DateRangePickerWidget.component.tsx
const DateRangePickerWidgetBase = (props) => {
	const convertToDateTime = React.useMemo(() => props.withTime ? dateConverters.toDateTimeAria : dateConverters.toDateAria, [props.withTime]);
	const normalizedValue = React.useMemo(() => props.value && {
		start: props.value.start ? convertToDateTime(props.value.start) : null,
		end: props.value.end ? convertToDateTime(props.value.end) : null
	}, [props.value, convertToDateTime]);
	const normalizedMaxValue = React.useMemo(() => props.maxValue && convertToDateTime(props.maxValue), [props.maxValue, convertToDateTime]);
	const normalizedMinValue = React.useMemo(() => props.minValue && convertToDateTime(props.minValue), [props.minValue, convertToDateTime]);
	const [timeVisible, setTimeVisible] = React.useState(props.withTime);
	const handleChange = (value) => {
		props.onChange?.({
			start: value?.start ? dateConverters.fromAria(value.start) ?? null : null,
			end: value?.end ? dateConverters.fromAria(value.end) ?? null : null
		});
	};
	const state = useDateRangePickerState({
		...props,
		value: normalizedValue,
		maxValue: normalizedMaxValue,
		minValue: normalizedMinValue,
		onChange: handleChange,
		granularity: "day"
	});
	const rootRef = React.useRef(null);
	const { startFieldProps, endFieldProps, calendarProps, groupProps } = useDateRangePicker({
		...props,
		value: normalizedValue,
		maxValue: normalizedMaxValue,
		minValue: normalizedMinValue,
		onChange: handleChange,
		granularity: "day"
	}, state, rootRef);
	const onClear = () => {
		state.setValue(null);
	};
	const dateStartIsValid = dateIsValid(state.dateRange?.start);
	const dateEndIsValid = dateIsValid(state.dateRange?.end);
	const isStartErrors = state.dateRange?.start && state.validationState === "invalid";
	const isEndErrors = state.dateRange?.end && state.validationState === "invalid";
	const isStartValid = state.dateRange?.start && !isStartErrors && dateStartIsValid;
	const isEndValid = state.dateRange?.end && !isEndErrors && dateEndIsValid;
	const startDateLabel = dateStartIsValid && state.dateRange?.start ? dayjs(state.dateRange.start.toString()).format("DD.MM.YY") : "__.__.__";
	const endDateLabel = dateEndIsValid && state.dateRange?.end ? dayjs(state.dateRange.end.toString()).format("DD.MM.YY") : "__.__.__";
	return /* @__PURE__ */ jsxs("div", {
		"data-cy": "dateRangePickerWidgetComponent-div",
		ref: rootRef,
		className: cn("relative inline-flex flex-col rounded-xl bg-white text-left shadow-md", props.className),
		children: [/* @__PURE__ */ jsx("div", {
			className: "min-w-[15.625rem] border-b border-gray-200 px-6 pb-4 pt-6",
			children: timeVisible ? /* @__PURE__ */ jsx(DateRangeTimeFields, {
				groupProps,
				size: props.size ?? "sm",
				readonly: props.readonly,
				startDateLabel,
				endDateLabel,
				startTimeValue: state.timeRange?.start ?? void 0,
				endTimeValue: state.timeRange?.end ?? void 0,
				onChangeStart: (time) => state.setTimeRange({
					start: time ?? null,
					end: state.timeRange?.end ?? null
				}),
				onChangeEnd: (time) => state.setTimeRange({
					start: state.timeRange?.start ?? null,
					end: time ?? null
				})
			}) : /* @__PURE__ */ jsxs(Container, {
				...groupProps,
				className: "group",
				size: props.size,
				isErrors: Boolean(isStartErrors || isEndErrors || props.error),
				isValid: Boolean(isStartValid && isEndValid),
				isReadonly: props.readonly,
				children: [
					/* @__PURE__ */ jsx(DateField, {
						...startFieldProps,
						size: props.size ?? "sm",
						isDisabled: props.readonly,
						hideTimeZone: true
					}),
					/* @__PURE__ */ jsx("span", { children: " - " }),
					/* @__PURE__ */ jsx(DateField, {
						...endFieldProps,
						size: props.size ?? "sm",
						isDisabled: props.readonly,
						hideTimeZone: true
					})
				]
			})
		}), /* @__PURE__ */ jsx(RangeCalendar, {
			...calendarProps,
			onClear: props.clearable ? onClear : void 0,
			className: "mx-auto",
			onTimeVisibleChange: props.withTime ? setTimeVisible : void 0,
			timeVisible
		})]
	});
};
const DateRangePickerWidget = (props) => {
	const methods = useFormContext();
	return props.controlled ? /* @__PURE__ */ jsx(Controller, {
		name: props.name ?? "",
		control: methods.control,
		render: ({ field, fieldState }) => /* @__PURE__ */ jsx(DateRangePickerWidgetBase, {
			...props,
			error: fieldState.error?.message,
			onChange: field.onChange,
			value: field.value,
			onBlur: field.onBlur
		})
	}) : /* @__PURE__ */ jsx(DateRangePickerWidgetBase, { ...props });
};
//#endregion
export { DateRangePickerWidget as default };

//# sourceMappingURL=DateRangePickerWidget.component.js.map